import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { obsService } from "@/features/obs/services/obsService";
import { obsReportService } from "@/features/obs/services/obsReportService";
import { useMyPermissions } from "@/features/permissions/hooks/usePermissionsQueries";
import type {
  ObsCreateReportInput,
  ObsReport,
  ObsTileResult,
  ObsUploadResult,
  ObsValidationReport,
  ObsWorkbookProfile,
} from "@/types/obs";

export const obsKeys = {
  all: ["obs-datasets"] as const,
  list: () => [...obsKeys.all, "list"] as const,
};

/** Does the signed-in user hold `obs:manage`? SUPER_ADMIN-only by baseline. */
export function useCanManageObs(): boolean {
  const { data } = useMyPermissions();
  return (data?.permissions ?? []).includes("obs:manage");
}

export function useObsScreens(enabled = true) {
  return useQuery({
    queryKey: obsKeys.list(),
    queryFn: () => obsService.list(),
    enabled,
  });
}

/**
 * Dry run. Deliberately NOT a query: it is an operator action against a file
 * they just picked, must never be served from cache, and must never re-run on
 * a window focus.
 */
export function useValidateObsWorkbook() {
  return useMutation<ObsValidationReport, Error, { slug: string; file: File }>({
    mutationFn: ({ slug, file }) => obsService.validate(slug, file),
  });
}

export function useUploadObsWorkbook() {
  const queryClient = useQueryClient();
  return useMutation<
    ObsUploadResult,
    Error,
    { slug: string; file: File; force?: boolean }
  >({
    mutationFn: ({ slug, file, force }) => obsService.upload(slug, file, force),
    onSuccess: (result) => {
      // A rejected or unconfirmed upload changed nothing on the server, so
      // refetching would only churn. Only invalidate when something published.
      if (result.applied) {
        queryClient.invalidateQueries({ queryKey: obsKeys.all });
      }
    },
  });
}

export function useActivateObsVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, version }: { slug: string; version: number }) =>
      obsService.activateVersion(slug, version),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: obsKeys.all }),
  });
}

/* ------------------------------------------------------------------------- */
/* Reports an operator creates                                               */
/* ------------------------------------------------------------------------- */

export const obsReportKeys = {
  all: ["obs-reports"] as const,
  list: () => [...obsReportKeys.all, "list"] as const,
};

export function useObsReports(enabled = true) {
  return useQuery({
    queryKey: obsReportKeys.list(),
    queryFn: () => obsReportService.list(),
    enabled,
  });
}

/**
 * Read a workbook and propose what its columns mean.
 *
 * A mutation, not a query: it is an operator action against a file they just
 * picked, must never be served from cache, and must never re-run on a window
 * focus. Nothing is stored by it.
 */
export function useProfileObsWorkbook() {
  return useMutation<ObsWorkbookProfile, Error, { file: File }>({
    mutationFn: ({ file }) => obsReportService.profile(file),
  });
}

export function useCreateObsReport() {
  const queryClient = useQueryClient();
  return useMutation<ObsReport, Error, ObsCreateReportInput>({
    mutationFn: (input) => obsReportService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: obsReportKeys.all });
      // A new report also appears in the dataset list, which is what the upload
      // cards are built from.
      queryClient.invalidateQueries({ queryKey: obsKeys.all });
    },
  });
}

export function useUpdateObsReport() {
  const queryClient = useQueryClient();
  return useMutation<ObsReport, Error, { slug: string; patch: Partial<ObsReport> }>({
    mutationFn: ({ slug, patch }) => obsReportService.update(slug, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: obsReportKeys.all });
      queryClient.invalidateQueries({ queryKey: obsKeys.all });
    },
  });
}

/**
 * The charts this report's data can draw. Enabled only once there is data —
 * before that the answer is trivially empty and the request is noise.
 */
export function useObsChartOptions(slug: string | null, enabled = true) {
  return useQuery({
    queryKey: [...obsReportKeys.all, "chart-options", slug] as const,
    queryFn: () => obsReportService.chartOptions(slug as string),
    enabled: Boolean(slug) && enabled,
  });
}

export function useUploadObsTile() {
  const queryClient = useQueryClient();
  return useMutation<
    ObsTileResult,
    Error,
    { slug: string; file: File; locale?: "ar" | "en" }
  >({
    mutationFn: ({ slug, file, locale }) =>
      obsReportService.uploadTile(slug, file, locale ?? "ar"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: obsReportKeys.all }),
  });
}

export function useDeleteObsReport() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { slug: string }>({
    mutationFn: ({ slug }) => obsReportService.remove(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: obsReportKeys.all });
      queryClient.invalidateQueries({ queryKey: obsKeys.all });
    },
  });
}
